import { OFFER_NOTE_CONTACT_MESSAGE } from '../lib/offerNoteContact';
// Copy for the public RFQ quote page (/rfq/:token/quote) and the stage-1
// verification page (/supplier/verify), opened from a link posted to Facebook
// groups, WhatsApp or TikTok.
//
// TRILINGUAL (ar / en / zh) as of 2026-09-19. It shipped English-only, which
// was wrong twice over: golden rule 3 asks for all three wherever buyers and
// suppliers meet, and this page's whole audience is Chinese factories reading a
// link a stranger sent them. An English wall at that moment is not a language
// problem, it is the end of the funnel — the reader does not complain, they
// close the tab.
//
// The page keys off the named codes submit_public_rfq_quote and
// submit_supplier_docs raise, and every language pack answers the same key set,
// so a missing translation degrades to English rather than to a raw plpgsql
// string. Same shape and reason as claimStrings.js.
//
// WHAT IS NOT HERE: the quote FORM's own labels. PublicRfqQuote builds its copy
// as `{ ...publicRfqStrings(lang), ...quoteStrings(lang) }` — quoteStrings wins
// every collision, and it has been trilingual all along. The 24 form keys this
// file used to carry (price, moq, leadTime, priceBases, validDays, the sample
// trio, …) were dead: overridden on the quote page and unused on the
// verification page. They are deleted rather than translated, because a
// translated dead key is worse than an English one — it looks maintained, and
// the next person to edit it will wonder for an hour why nothing changed.

export const PUBLIC_RFQ_STRINGS = {
  ar: {
    // ── page chrome ──────────────────────────────────────────────────────
    eyebrow: 'طلب عرض سعر',
    via: 'عبر مَعبر',
    ref: 'المرجع',
    postedOn: 'نُشر',
    // Arabic counts in four shapes, not two. Getting this wrong is the kind of
    // error a native reader clocks instantly and reads as "nobody here speaks
    // my language", on a page whose entire job is to be trusted by a stranger.
    quotesReceived: (n) => {
      if (n === 1) return 'وصل عرض سعر واحد';
      if (n === 2) return 'وصل عرضا سعر';
      if (n >= 3 && n <= 10) return `وصلت ${n} عروض أسعار`;
      return `وصل ${n} عرض سعر`;
    },
    beFirst: 'كن أول من يقدّم عرضاً',

    // ── the request ──────────────────────────────────────────────────────
    product: 'المنتج',
    category: 'الفئة',
    deadline: 'آخر موعد للرد',
    // No noDeadline string on purpose: when response_deadline is null the row
    // is omitted entirely. "No deadline set" reads as an RFQ nobody is waiting
    // on, which is the opposite of what the link is for.

    // The LABEL for the buyer row. Added 2026-09-20: that row used to reuse
    // `eyebrow` as its label, so the card read «طلب عرض سعر: مشترٍ مسجَّل — جدة»
    // — the page's own title used as a field name. `eyebrow` is untouched and
    // still correct where it belongs, at the top of the page.
    //
    // «نُشر بواسطة» rather than «المشتري»: the value is a STATUS plus a city,
    // not a name, so «المشتري: مشترٍ مسجَّل» would echo itself. It also reuses
    // `postedOn` ('نُشر') from two lines above, so the page keeps one register.
    postedBy: 'نُشر بواسطة',
    // The trader is identified by city and registration status only — never by
    // name (owner's decision, 2026-09-18). This page is posted publicly, and a
    // name in a Facebook link preview cannot be withdrawn later.
    buyerRegistered: 'مشترٍ مسجَّل',
    buyerVerified: 'مشترٍ موثَّق',

    // ── signed-out CTA ───────────────────────────────────────────────────
    ctaTitle: 'سجّل لتقديم عرض سعرك',
    ctaBody: 'قراءة هذا الطلب مجانية. تقديم عرض السعر يتطلّب حساب مورّد في مَعبر — دقيقة تقريباً، وتعود إلى هنا مباشرة.',
    ctaButton: 'سجّل لتقديم عرض سعرك',
    haveAccount: 'لديك حساب؟ سجّل الدخول',

    // ── request details (registered suppliers only) ──────────────────────
    detailsTitle: 'تفاصيل الطلب',
    detailsLocked: 'سجّل لعرض المواصفات الكاملة — الخامات والتشطيب والتغليف وشروط الدفع لدى المشتري.',
    spec: 'المواصفات',
    customization: 'التخصيص',
    attachments: 'المرفقات',
    attachmentsHint: 'ملفات رفعها المشتري مع الطلب. تفتح لمدة 10 دقائق ثم تحتاج تحديث الصفحة.',
    attachmentOpen: 'فتح',
    attachmentUnavailable: 'تعذّر فتح هذا الملف. حدّث الصفحة وحاول مرة أخرى.',

    // ── the form (chrome only — labels come from quoteStrings) ───────────
    formTitle: 'عرض سعرك',
    submit: 'أرسل عرض السعر',
    submitting: 'جارٍ الإرسال…',
    required: 'مطلوب',

    // ── after submission ─────────────────────────────────────────────────
    // The quote is saved and pending. Verification does not block submission —
    // it decides whether the quote reaches the buyer.
    doneTitle: 'تم إرسال عرض السعر',
    doneBody: 'حُفظ عرض سعرك. خطوة واحدة متبقّية قبل أن يصل إلى المشتري: ارفع رخصتك التجارية للمراجعة — تستغرق دقيقتين تقريباً.',
    doneCta: 'أكمل التوثيق',

    // ── link / request states ────────────────────────────────────────────
    stateLoading: 'جارٍ التحميل…',
    states: {
      not_found: 'هذا الرابط غير صالح. ربما كُتب بشكل خاطئ أو استُبدل برابط أحدث.',
      revoked: 'أوقفت مَعبر هذا الرابط. قد يكون الطلب ما زال مفتوحاً — تواصل معنا وسنرسل لك رابطاً حالياً.',
      expired: 'انتهت صلاحية هذا الرابط. قد يكون الطلب ما زال مفتوحاً — تواصل معنا وسنرسل لك رابطاً حالياً.',
      closed: 'أُغلق هذا الطلب ولم يعد يستقبل عروض أسعار. سجّل على أي حال وسنرسل لك الطلب التالي في فئتك.',
    },

    // ── stage-1 verification page (/supplier/verify) ─────────────────────
    // The VISIBILITY bar, not the payout file: licence + one contact channel
    // + category. See CLAUDE.md golden rule 2a.
    verifyTitle: 'خطوة أخيرة',
    verifyBody: 'حُفظ عرض سعرك. ارفع رخصتك التجارية لنتمكّن من اعتمادك — حتى ذلك الحين لا يستطيع المشتري رؤية عرضك. تستغرق دقيقتين تقريباً.',
    verifyLicence: 'الرخصة التجارية',
    verifyLicenceHint: 'صورة أو ملف PDF.',
    verifyUpload: 'ارفع الرخصة',
    verifyUploading: 'جارٍ الرفع…',
    verifyUploaded: 'تم الرفع ✓',
    verifyReplace: 'استبدال',
    verifyContact: 'واتساب أو ويتشات',
    verifyContactHint: 'واحد على الأقل — عبره نتواصل معك بشأن ردّ المشتري.',
    verifyWhatsapp: 'رقم الواتساب',
    verifyWechat: 'معرّف ويتشات',
    verifyCategory: 'فئة المنتج الرئيسية',
    verifyStore: 'رابط متجر علي بابا أو 1688',
    verifyStoreHint: 'اختياري.',
    verifySubmit: 'أرسل للمراجعة',
    verifySubmitting: 'جارٍ الإرسال…',
    verifySkip: 'تخطَّ الآن',
    // Skipping is a supported outcome, not a failure. Say what survives it,
    // because the fear that stops people is that the quote is lost.
    verifySkipNote: 'عرض سعرك وحسابك محفوظان في كل الأحوال. يمكنك العودة إلى هذه الخطوة من لوحة التحكم في أي وقت.',
    verifyDoneTitle: 'قيد المراجعة',
    verifyDoneBody: 'لدينا كل ما نحتاجه. بمجرّد اعتمادك يصل عرض سعرك إلى المشتري — وأي عرض ترسله لاحقاً يظهر مباشرة.',
    verifyDoneCta: 'اذهب إلى لوحة التحكم',
    verifyUploadFailed: 'تعذّر رفع الملف. جرّب صورة أصغر أو ملف PDF.',

    // ── named errors from submit_supplier_docs ───────────────────────────
    docsErrors: {
      DOCS_AUTH_REQUIRED: 'انتهت جلستك. سجّل الدخول من جديد — لم يُفقد شيء ممّا رفعته.',
      DOCS_NOT_A_SUPPLIER: 'هذا الحساب مسجَّل كمشترٍ، لا كمورّد.',
      DOCS_UNCONFIRMED_IDENTITY: 'لم يُؤكَّد هاتفك أو بريدك بعد. سجّل الدخول من جديد وأدخل الرمز الذي نرسله لك.',
      DOCS_MISSING_LICENSE: 'ارفع رخصتك التجارية قبل الإرسال.',
      DOCS_MISSING_CONTACT: 'أضف رقم واتساب أو معرّف ويتشات — واحد على الأقل.',
      DOCS_MISSING_CATEGORY: 'اختر فئة منتجك الرئيسية.',
      FALLBACK: 'تعذّر إرسال ملفك. حاول مرة أخرى — وإن تكرّر الأمر تواصل معنا وسنتولّاه يدوياً.',
    },

    // ── named errors from submit_public_rfq_quote ────────────────────────
    errors: {
      RFQ_AUTH_REQUIRED: 'انتهت جلستك قبل حفظ عرض السعر. سجّل الدخول من جديد وستجد نموذج عرضك كما هو.',
      RFQ_NOT_A_SUPPLIER: 'هذا الحساب مسجَّل كمشترٍ، لا كمورّد. سجّل الخروج وأنشئ حساب مورّد لتقديم عرض على هذا الطلب.',
      RFQ_LINK_NOT_FOUND: 'هذا الرابط غير صالح. ربما كُتب بشكل خاطئ أو استُبدل برابط أحدث.',
      RFQ_LINK_REVOKED: 'أوقفت مَعبر هذا الرابط. قد يكون الطلب ما زال مفتوحاً — تواصل معنا وسنرسل لك رابطاً حالياً.',
      RFQ_LINK_EXPIRED: 'انتهت صلاحية هذا الرابط. قد يكون الطلب ما زال مفتوحاً — تواصل معنا وسنرسل لك رابطاً حالياً.',
      RFQ_CLOSED: 'توقّف هذا الطلب عن استقبال العروض أثناء تعبئتك للنموذج. لم يُفقد شيء ممّا أدخلته — تواصل معنا وسنوجّهك إلى طلب حالي.',
      RFQ_BAD_CURRENCY: 'اختر إحدى العملات المدرجة.',
      RFQ_BAD_PRICE_BASIS: 'اختر أحد أسس التسعير المدرجة.',
      RFQ_DUPLICATE_QUOTE: 'سبق أن قدّمت عرض سعر على هذا الطلب. افتح لوحة التحكم لتعديله بدلاً من إرسال عرض ثانٍ.',
      OFFER_NOTE_CONTACT_DETAILS: OFFER_NOTE_CONTACT_MESSAGE.ar,
      FALLBACK: 'تعذّر حفظ عرض سعرك. حاول مرة أخرى — وإن تكرّر الأمر تواصل معنا وسنتولّاه يدوياً.',
    },
  },

  en: {
    // ── page chrome ──────────────────────────────────────────────────────
    eyebrow: 'Request for quotation',
    via: 'via Maabar',
    ref: 'Ref',
    postedOn: 'Posted',
    quotesReceived: (n) => (n === 1 ? '1 quote received' : `${n} quotes received`),
    beFirst: 'Be the first to quote',

    // ── the request ──────────────────────────────────────────────────────
    product: 'Product',
    category: 'Category',
    deadline: 'Respond by',

    postedBy: 'Posted by',
    buyerRegistered: 'Registered Buyer',
    buyerVerified: 'Verified Buyer',

    // ── signed-out CTA ───────────────────────────────────────────────────
    ctaTitle: 'Register to submit your quote',
    ctaBody: 'Reading this request is free. Submitting a quote takes a Maabar supplier account — about a minute, and you come straight back here.',
    ctaButton: 'Register to submit your quote',
    haveAccount: 'Already have an account? Sign in',

    // ── request details (registered suppliers only) ──────────────────────
    // Unlocked by registration, not by the token: the public card above stays
    // minimal because it is posted to social media, and the free-text
    // description is where a trader leaks a phone number.
    detailsTitle: 'Request details',
    detailsLocked: 'Register to see the full specification — materials, finish, packaging and the buyer’s payment terms.',
    spec: 'Specification',
    customization: 'Customization',
    attachments: 'Attachments',
    attachmentsHint: 'Files the buyer uploaded with this request. Links stay open for 10 minutes, then the page needs a refresh.',
    attachmentOpen: 'Open',
    attachmentUnavailable: 'This file could not be opened. Refresh the page and try again.',

    // ── the form (chrome only — labels come from quoteStrings) ───────────
    formTitle: 'Your quote',
    submit: 'Submit quote',
    submitting: 'Submitting…',
    required: 'Required',

    // ── after submission ─────────────────────────────────────────────────
    doneTitle: 'Quote submitted',
    // The previous wording ("upload your business licence so it can be shown
    // to the buyer") read as though the LICENCE goes to the buyer. It does
    // not — it goes to Maabar for review, and what reaches the buyer is the
    // quote. Rewritten by the owner on 2026-09-19.
    doneBody: 'Your quote is saved. One step left before it reaches the buyer: upload your business licence for review — takes about two minutes.',
    doneCta: 'Complete verification',

    // ── link / request states ────────────────────────────────────────────
    stateLoading: 'Loading…',
    states: {
      not_found: 'This link is not valid. It may have been mistyped, or replaced with a newer one.',
      revoked: 'This link has been turned off by Maabar. The request may still be open — contact us and we will send you a current link.',
      expired: 'This link has expired. The request may still be open — contact us and we will send you a current link.',
      closed: 'This request is closed and is no longer accepting quotes. Register anyway and we will send you the next request in your category.',
    },

    // ── stage-1 verification page (/supplier/verify) ─────────────────────
    verifyTitle: 'One last step',
    verifyBody: 'Your quote is saved. Upload your business licence so we can approve you — until then the buyer cannot see your quote. It takes about two minutes.',
    verifyLicence: 'Business licence',
    verifyLicenceHint: 'Photo or PDF.',
    verifyUpload: 'Upload licence',
    verifyUploading: 'Uploading…',
    verifyUploaded: 'Uploaded ✓',
    verifyReplace: 'Replace',
    verifyContact: 'WhatsApp or WeChat',
    verifyContactHint: 'At least one — this is how we reach you about the buyer’s reply.',
    verifyWhatsapp: 'WhatsApp number',
    verifyWechat: 'WeChat ID',
    verifyCategory: 'Main product category',
    verifyStore: 'Alibaba or 1688 store link',
    verifyStoreHint: 'Optional.',
    verifySubmit: 'Submit for review',
    verifySubmitting: 'Submitting…',
    verifySkip: 'Skip for now',
    verifySkipNote: 'Your quote and your account are kept either way. You can come back to this from your dashboard at any time.',
    verifyDoneTitle: 'Under review',
    verifyDoneBody: 'We have everything we need. Once we approve you, your quote goes to the buyer — and any future quote you send is visible straight away.',
    verifyDoneCta: 'Go to my dashboard',
    verifyUploadFailed: 'The file could not be uploaded. Try a smaller photo or a PDF.',

    // ── named errors from submit_supplier_docs ───────────────────────────
    docsErrors: {
      DOCS_AUTH_REQUIRED: 'Your session ended. Sign in again — nothing you uploaded was lost.',
      DOCS_NOT_A_SUPPLIER: 'This account is registered as a buyer, not a supplier.',
      DOCS_UNCONFIRMED_IDENTITY: 'Your phone or email is not confirmed yet. Sign in again and enter the code we send you.',
      DOCS_MISSING_LICENSE: 'Upload your business licence before submitting.',
      DOCS_MISSING_CONTACT: 'Add a WhatsApp number or a WeChat ID — at least one.',
      DOCS_MISSING_CATEGORY: 'Choose your main product category.',
      FALLBACK: 'Your file could not be submitted. Please try once more — if it happens again, contact us and we will take it by hand.',
    },

    // ── named errors from submit_public_rfq_quote ────────────────────────
    errors: {
      RFQ_AUTH_REQUIRED: 'Your session ended before the quote was saved. Sign in again and your quote form will still be here.',
      RFQ_NOT_A_SUPPLIER: 'This account is registered as a buyer, not a supplier. Sign out and register a supplier account to quote on this request.',
      RFQ_LINK_NOT_FOUND: 'This link is not valid. It may have been mistyped, or replaced with a newer one.',
      RFQ_LINK_REVOKED: 'This link has been turned off by Maabar. The request may still be open — contact us and we will send you a current link.',
      RFQ_LINK_EXPIRED: 'This link has expired. The request may still be open — contact us and we will send you a current link.',
      RFQ_CLOSED: 'This request stopped accepting quotes while you were filling the form. Nothing you entered was lost — contact us and we will point you at a current request.',
      RFQ_BAD_CURRENCY: 'Choose one of the listed currencies.',
      RFQ_BAD_PRICE_BASIS: 'Choose one of the listed price bases.',
      RFQ_DUPLICATE_QUOTE: 'You have already submitted a quote on this request. Open your dashboard to edit it rather than sending a second one.',
      OFFER_NOTE_CONTACT_DETAILS: OFFER_NOTE_CONTACT_MESSAGE.en,
      FALLBACK: 'Your quote could not be saved. Please try once more — if it happens again, contact us and we will take it by hand.',
    },
  },

  zh: {
    // ── page chrome ──────────────────────────────────────────────────────
    eyebrow: '询价单',
    via: '来自 Maabar',
    ref: '编号',
    postedOn: '发布',
    quotesReceived: (n) => `已收到 ${n} 份报价`,
    beFirst: '成为第一个报价的供应商',

    // ── the request ──────────────────────────────────────────────────────
    product: '产品',
    category: '类目',
    deadline: '报价截止',

    postedBy: '发布者',
    buyerRegistered: '已注册买家',
    buyerVerified: '已认证买家',

    // ── signed-out CTA ───────────────────────────────────────────────────
    ctaTitle: '注册后提交报价',
    ctaBody: '阅读本询价单是免费的。提交报价需要一个 Maabar 供应商账号——大约一分钟，完成后直接回到本页。',
    ctaButton: '注册后提交报价',
    haveAccount: '已有账号？登录',

    // ── request details (registered suppliers only) ──────────────────────
    detailsTitle: '询价详情',
    detailsLocked: '注册后查看完整规格——材料、表面处理、包装以及买家的付款条件。',
    spec: '规格说明',
    customization: '定制要求',
    attachments: '附件',
    attachmentsHint: '买家随询价单上传的文件。链接有效期 10 分钟，过期后请刷新页面。',
    attachmentOpen: '打开',
    attachmentUnavailable: '无法打开此文件。请刷新页面后重试。',

    // ── the form (chrome only — labels come from quoteStrings) ───────────
    formTitle: '您的报价',
    submit: '提交报价',
    submitting: '提交中…',
    required: '必填',

    // ── after submission ─────────────────────────────────────────────────
    doneTitle: '报价已提交',
    doneBody: '您的报价已保存。送达买家前还差一步：上传营业执照供我们审核——大约需要两分钟。',
    doneCta: '完成认证',

    // ── link / request states ────────────────────────────────────────────
    stateLoading: '加载中…',
    states: {
      not_found: '此链接无效。可能是输入有误，或已被新链接取代。',
      revoked: 'Maabar 已停用此链接。该询价可能仍然有效——请联系我们，我们会发送一个新链接。',
      expired: '此链接已过期。该询价可能仍然有效——请联系我们，我们会发送一个新链接。',
      closed: '此询价已关闭，不再接受报价。仍欢迎注册，我们会把您类目下的下一个询价发给您。',
    },

    // ── stage-1 verification page (/supplier/verify) ─────────────────────
    verifyTitle: '最后一步',
    verifyBody: '您的报价已保存。请上传营业执照以便我们审核通过——在此之前买家看不到您的报价。大约需要两分钟。',
    verifyLicence: '营业执照',
    verifyLicenceHint: '照片或 PDF。',
    verifyUpload: '上传营业执照',
    verifyUploading: '上传中…',
    verifyUploaded: '已上传 ✓',
    verifyReplace: '重新上传',
    verifyContact: '微信或 WhatsApp',
    verifyContactHint: '至少填写一项——买家回复后我们通过它联系您。',
    verifyWhatsapp: 'WhatsApp 号码',
    verifyWechat: '微信号',
    verifyCategory: '主营产品类目',
    verifyStore: '阿里巴巴或 1688 店铺链接',
    verifyStoreHint: '选填。',
    verifySubmit: '提交审核',
    verifySubmitting: '提交中…',
    verifySkip: '暂时跳过',
    verifySkipNote: '无论如何，您的报价和账号都会保留。您随时可以从后台回到这一步。',
    verifyDoneTitle: '审核中',
    verifyDoneBody: '资料已齐全。审核通过后，您的报价即会送达买家——之后提交的报价将直接可见。',
    verifyDoneCta: '进入我的后台',
    verifyUploadFailed: '文件上传失败。请尝试更小的图片或 PDF。',

    // ── named errors from submit_supplier_docs ───────────────────────────
    docsErrors: {
      DOCS_AUTH_REQUIRED: '登录状态已过期。请重新登录——您已上传的内容不会丢失。',
      DOCS_NOT_A_SUPPLIER: '此账号注册为买家，而非供应商。',
      DOCS_UNCONFIRMED_IDENTITY: '您的手机号或邮箱尚未验证。请重新登录并输入我们发送的验证码。',
      DOCS_MISSING_LICENSE: '请先上传营业执照再提交。',
      DOCS_MISSING_CONTACT: '请填写 WhatsApp 号码或微信号——至少一项。',
      DOCS_MISSING_CATEGORY: '请选择您的主营产品类目。',
      FALLBACK: '资料提交失败。请再试一次——如果仍然失败，请联系我们，我们会人工处理。',
    },

    // ── named errors from submit_public_rfq_quote ────────────────────────
    errors: {
      RFQ_AUTH_REQUIRED: '报价保存前登录状态已过期。请重新登录，您填写的报价表仍在。',
      RFQ_NOT_A_SUPPLIER: '此账号注册为买家，而非供应商。请退出登录并注册供应商账号后再报价。',
      RFQ_LINK_NOT_FOUND: '此链接无效。可能是输入有误，或已被新链接取代。',
      RFQ_LINK_REVOKED: 'Maabar 已停用此链接。该询价可能仍然有效——请联系我们，我们会发送一个新链接。',
      RFQ_LINK_EXPIRED: '此链接已过期。该询价可能仍然有效——请联系我们，我们会发送一个新链接。',
      RFQ_CLOSED: '您填写表单期间，此询价已停止接受报价。您输入的内容不会丢失——请联系我们，我们会为您推荐正在进行的询价。',
      RFQ_BAD_CURRENCY: '请从列出的币种中选择。',
      RFQ_BAD_PRICE_BASIS: '请从列出的价格条款中选择。',
      RFQ_DUPLICATE_QUOTE: '您已对此询价提交过报价。请在后台修改，不要重复提交。',
      OFFER_NOTE_CONTACT_DETAILS: OFFER_NOTE_CONTACT_MESSAGE.zh,
      FALLBACK: '报价保存失败。请再试一次——如果仍然失败，请联系我们，我们会人工处理。',
    },
  },
};

// Matched on lowercase SUBSTRING, for the two reasons claimErrorText gives:
// plpgsql prefixes its own context onto the message, and nothing guarantees the
// casing survives the client library.
//
// Ordered longest-first so a shorter code can never shadow a longer one that
// contains it. Today none overlap, but the list is the thing that would stop
// being true quietly when someone adds RFQ_CLOSED_EARLY next to RFQ_CLOSED.
//
// Derived from `en` on purpose: the codes are identifiers raised by Postgres,
// identical in every pack, and pinning the list to one pack means a translator
// who drops a key breaks a message rather than breaking the matcher.
const ERROR_CODES = Object.keys(PUBLIC_RFQ_STRINGS.en.errors)
  .filter((code) => code !== 'FALLBACK')
  .sort((a, b) => b.length - a.length);

export function publicRfqErrorText(error, t) {
  const raw = String(error?.message || error || '').toLowerCase();
  const hit = ERROR_CODES.find((code) => raw.includes(code.toLowerCase()));
  return t.errors[hit] || t.errors.FALLBACK;
}

const DOCS_ERROR_CODES = Object.keys(PUBLIC_RFQ_STRINGS.en.docsErrors)
  .filter((code) => code !== 'FALLBACK')
  .sort((a, b) => b.length - a.length);

// Same matching rule as publicRfqErrorText, against the stage-1 verification
// codes. Kept as its own map rather than merged: the two RPCs are separate
// surfaces, and a shared bag would let a quote error render verification copy.
export function supplierDocsErrorText(error, t) {
  const raw = String(error?.message || error || '').toLowerCase();
  const hit = DOCS_ERROR_CODES.find((code) => raw.includes(code.toLowerCase()));
  return t.docsErrors[hit] || t.docsErrors.FALLBACK;
}

export function publicRfqStrings(lang) {
  return PUBLIC_RFQ_STRINGS[lang] || PUBLIC_RFQ_STRINGS.en;
}
